import { Button, Switch } from "antd";
import { useEffect, useState } from "react";
import TopCard from "../../../../components/common/TopCard";
import Loader from "../../../../components/ui/Loader";
import {
  useGetMonthlyPayCycleQuery,
  useGetPayrollSettingQuery,
  useUpdatePayrollEnableAndPayScheduleMutation,
} from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { payrollSettingsTabList } from "./payrollSetting";

type PayScheduleDataType = {
  is_enable: number;
  pay_day_type?: "fixed_date" | "last_working_day";
  pay_day?: number;
};

export default function PaySchedule() {
  const [payScheduleData, setPayScheduleData] = useState<
    PayScheduleDataType | undefined
  >(undefined);
  const { data, isLoading, isError, error } = useGetPayrollSettingQuery();
  const {
    isLoading: isCycleLoading,
    isFetching: isCycleFetching,
    isError: isCycleError,
    error: cycleError,
    data: cycleData,
  } = useGetMonthlyPayCycleQuery(
    {
      params: {
        pay_day_type: payScheduleData?.pay_day_type,
        pay_day:
          payScheduleData?.pay_day_type === "fixed_date"
            ? payScheduleData?.pay_day
            : undefined,
      },
    },
    {
      skip: true,
      // skip: !payScheduleData?.pay_day_type,
    }
  );

  const [
    updatePayrollEnableAndPaySchedule,
    {
      isLoading: isPayLoading,
      isError: isPayError,
      error: payError,
      isSuccess: isPaySuccess,
    },
  ] = useUpdatePayrollEnableAndPayScheduleMutation();

  const paySchedule = data?.data.payroll_setting.pay_components.pay_schedule;
  const payrollSettingId = data?.data.payroll_setting.id;

  useError(isError, error, "Get salary breakdown data fail");
  useError(isCycleError, cycleError, "Get monthly pay cycle data fail");
  useError(isPayError, payError, "Update pay schedule data fail");

  const handlePaySchedule = <K extends keyof PayScheduleDataType>(
    key: K,
    value: PayScheduleDataType[K]
  ) => {
    setPayScheduleData(
      (prev) =>
        ({
          ...prev,
          [key]: value,
        } as PayScheduleDataType)
    );
  };

  useEffect(() => {
    setPayScheduleData({
      is_enable: paySchedule?.is_enable || 0,
      pay_day_type: paySchedule?.pay_day_type,
      pay_day: paySchedule?.pay_day,
    });
  }, [paySchedule?.is_enable, paySchedule?.pay_day, paySchedule?.pay_day_type]);

  useEffect(() => {
    if (isPaySuccess) {
      toast("success", "Update pay schedule successfully");
    }
  }, [isPaySuccess]);

  return (
    <SidebarLayout title="Pay Schedule" tabList={payrollSettingsTabList}>
      <TopCard
        leftChildren={
          <span className="w-full text-primary font-normal text-base">
            Please click the <span className="font-semibold">'Update'</span>{" "}
            button to apply any changes
          </span>
        }
      >
        {/* <Button className="normal-dark-button modal-button" size="middle">
          Cancel
        </Button> */}
        <Button
          className="modal-button"
          type="primary"
          size="middle"
          loading={isPayLoading}
          onClick={() => {
            updatePayrollEnableAndPaySchedule({
              businessId: payrollSettingId,
              data: payScheduleData,
            });
          }}
        >
          Update
        </Button>
      </TopCard>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="grid grid-cols-2 gap-6 items-start ">
          {/* <div className="bg-white rounded-md border border-extra-light p-6">
            <h4 className="text-header text-xl font-semibold mb-6">
              Pay Day (
              {underscoreToCapitalizeString(
                paySchedule?.payment_schedule || ""
              )}
              )
            </h4>

            <div className="text-header text-base font-medium mb-3">
              Choose when the payment will execute
            </div>
            <Radio.Group
              className="custom-radio w-full"
              size="large"
              value={payScheduleData?.pay_day_type}
              onChange={(e) =>
                handlePaySchedule("pay_day_type", e.target.value)
              }
            >
              <Radio
                value="last_working_day"
                className=" text-header text-base font-medium mr-3.5 mb-4"
              >
                Last working day of every month
              </Radio>
              <div className="flex gap-x-2 items-center w-full text-header text-base font-medium">
                <Radio value="fixed_date">Day</Radio>
                <Select
                  placeholder="1"
                  suffixIcon={<CaretDown />}
                  className="min-w-[120px]"
                  options={selectStringOption(
                    [...Array(28)].map((_, idx) => (idx + 1).toString())
                  )}
                  value={payScheduleData?.pay_day}
                  onChange={(val) => handlePaySchedule("pay_day", Number(val))}
                />
                <div className="ml-2">of every month</div>
              </div>
            </Radio.Group>

            {isCycleLoading || isCycleFetching ? (
              <Loader />
            ) : (
              <ul className="grid grid-cols-2 border border-light last:border-b-0 mt-6">
                <li className="min-h-[46px] inline-flex items-center px-3 py-2.5 border-b border-light border-r">
                  Cycle
                </li>
                <li className="min-h-[46px] inline-flex items-center px-3 py-2.5 border-b border-light">
                  Days
                </li>
                {cycleDemoList?.map((item, idx) => (
                  <Fragment key={idx}>
                    <li className="min-h-[46px] inline-flex items-center px-3 py-2.5 border-b border-light border-r">
                      {item?.cycle}
                    </li>
                    <li className="min-h-[46px] inline-flex items-center px-3 py-2.5 border-b border-light">
                      {item?.days}
                    </li>
                  </Fragment>
                ))}
              </ul>
            )}
          </div> */}
          <div className="bg-white p-4 rounded-md border border-extra-light">
            <h4 className="text-header text-xl font-semibold mb-6">
              Payroll System Status
            </h4>

            <div className="flex justify-between text-header text-base font-medium">
              <h6 className="text-base">
                Enable your payroll system from here
              </h6>

              <span className="flex items-center gap-2 text-sm">
                <Switch
                  checked={Boolean(payScheduleData?.is_enable)}
                  onChange={(checked) =>
                    handlePaySchedule("is_enable", Number(checked))
                  }
                />{" "}
                {payScheduleData?.is_enable ? "Enabled" : "Disabled"}
              </span>
            </div>
            <p className="text-display-body text-sm mt-4">
              This features allows you to disburse your employee's salary with
              ease and feasibility.
            </p>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
}

const cycleDemoList = [
  { cycle: "January (Current)", days: "Dec 31 - Jan 29" },
  { cycle: "February (Next)", days: "Jan 30 - Feb 26" },
];
